/**
 * EventDetailsPanel — At-a-glance event detail surface for the My Rodeos
 * expanded list. Renders inside an open `EventSection` so a delegate can see
 * the operationally important event fields without having to open the Edit
 * Event dialog or navigate to the wizard.
 *
 * Sections, in order:
 *   1. Identity & status — discipline, custom name, category, key flags
 *   2. Pricing — entry/txn fees, prize pool, surcharges
 *   3. Capacity & registration — caps, deadlines, multi-reg rules
 *   4. Format — total rounds, elimination, event format, status
 *
 * Handicap / Draw / Rodeo / Rodeo-description blocks were intentionally
 * removed — the rodeo header above the event list already conveys the
 * rodeo-level context, and handicap/draw rarely drive an "edit or not"
 * decision from this surface.
 */

import { useMemo, type ReactNode } from 'react';
import { formatCurrency, formatDate } from '@/utils/formatters';
import { Badge } from '@/components/ui/badge';
import { useEventStatusesList } from '@/api/generated_hooks/generated-event_statuses-hooks';

/** Loose typing — the events row flows through generated CRUD with an index signature. */
export interface EventDetailsPanelProps {
  event: any;
  /** Unused at the moment but kept to preserve the call site's API. */
  rodeo?: any;
  /**
   * Net prize pool from the canonical compute engine (after org-default +
   * custom adjustments, and dynamic per-entry × live entry count). When
   * provided, it replaces the raw `total_prize_amount` base for the "Prize
   * pool" row so the figure matches the live header amount. Left undefined
   * while the compute request is in flight.
   */
  prizePoolOverride?: number;
}

interface DetailRowProps {
  label: string;
  /** Pre-formatted display value. Falsy renders the dash placeholder. */
  value?: ReactNode;
  /** When true, value gets a slightly heavier weight (used for money). */
  emphasize?: boolean;
}

function DetailRow({ label, value, emphasize }: DetailRowProps) {
  const isEmpty =
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '');
  return (
    <div className="flex items-baseline justify-between gap-3 min-w-0">
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <span
        className={`text-xs text-right truncate ${
          emphasize ? 'font-medium text-foreground' : 'text-foreground/90'
        }`}
        title={typeof value === 'string' ? value : undefined}
      >
        {isEmpty ? <span className="text-muted-foreground/60">—</span> : value}
      </span>
    </div>
  );
}

interface SectionProps {
  title: string;
  children: ReactNode;
}

function Section({ title, children }: SectionProps) {
  return (
    <div className="rounded-md border border-border/60 bg-background p-3 space-y-2">
      <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h4>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function yesNo(v: unknown): string {
  return v ? 'Yes' : 'No';
}

export function EventDetailsPanel({ event, prizePoolOverride }: EventDetailsPanelProps) {
  const meta: Record<string, any> =
    (event && typeof event === 'object' && (event.metadata as any)) || {};
  const prizes: Record<string, any> =
    (meta && typeof meta === 'object' && meta.prizes) || {};

  const entryFee = Number(event?.entry_fee ?? 0);
  const txnFee = Number(event?.transaction_fee ?? 0);
  const totalCost = entryFee + (txnFee > 0 ? txnFee : 0);
  // Prefer the net computed pool (adjustments applied, dynamic resolved from
  // live entries) when the caller supplies it; otherwise fall back to the raw
  // configured base.
  const prizePool =
    prizePoolOverride !== undefined
      ? prizePoolOverride
      : Number(event?.total_prize_amount ?? prizes.total_prize_amount ?? 0);
  const isDynamicPrize = (event as any)?.prize_allocation_mode === 'dynamic';
  const basePrize = Number(event?.base_prize_amount ?? 0);
  const nonMemberSurcharge = Number(meta?.non_member_surcharge ?? 0);

  const eventCategory =
    (meta?.event_category as 'timed' | 'scored' | undefined) ?? null;
  const discipline =
    (typeof event?.trig_event_type_name === 'string' &&
      event.trig_event_type_name) ||
    (typeof event?.event_name === 'string' && event.event_name) ||
    '';
  const customName =
    typeof event?.event_name === 'string' &&
    event.event_name.trim().length > 0 &&
    event.event_name !== event?.trig_event_type_name
      ? event.event_name
      : '';

  const maxContestants = event?.max_contestants;
  const perPerf = event?.contestants_per_performance;
  const regDeadline = event?.registration_deadline;

  const totalRounds = event?.total_rounds;
  const eliminationEnabled = !!event?.elimination_enabled;

  const allowMultiReg = !!event?.allow_multiple_registrations;
  const maxPerUser = event?.max_registrations_per_user;

  // `events.event_status` is an FK UUID into `event_statuses`. The trigger
  // also denormalises the label onto `events.trig_status_name`, but that
  // field is `public: false` in the entity config so the generated CRUD
  // response strips it before it reaches us. Resolve the UUID → name with
  // a small lookup against the (six-row) statuses table; the query is
  // shared across rows by react-query's cache so it only fires once.
  const { data: statusesPage } = useEventStatusesList({ limit: 50 } as any);
  const statusIdToName = useMemo(() => {
    const raw = statusesPage as any;
    const rows: any[] = Array.isArray(raw) ? raw : (raw?.data ?? []);
    const map = new Map<string, string>();
    for (const r of rows) {
      if (r?.id && typeof r?.name === 'string') map.set(r.id, r.name);
    }
    return map;
  }, [statusesPage]);

  const rawStatus = event?.event_status;
  const statusDisplay: string | null =
    (typeof event?.trig_status_name === 'string' && event.trig_status_name) ||
    (typeof rawStatus === 'string' && statusIdToName.get(rawStatus)) ||
    (typeof rawStatus === 'string' && !rawStatus.includes('-') ? rawStatus : null);

  // Top status badges. Kept compact — only ones that matter operationally.
  const drawLocked = !!event?.draw_locked;
  const drawExecuted = !!event?.draw_executed;
  const statusBadges: { label: string; tone: string }[] = [];
  if (event?.is_published)
    statusBadges.push({ label: 'Published', tone: 'bg-green-600/15 text-green-600 border-green-600/30' });
  else
    statusBadges.push({ label: 'Draft', tone: 'bg-yellow-500/15 text-yellow-600 border-yellow-500/30' });
  if (eventCategory)
    statusBadges.push({
      label: eventCategory === 'timed' ? 'Timed' : 'Scored',
      tone: 'bg-muted text-foreground/80 border-border',
    });
  if (event?.is_mixed_gender)
    statusBadges.push({ label: 'Mixed gender', tone: 'bg-blue-500/15 text-blue-500 border-blue-500/30' });
  if (event?.is_century_jackpot)
    statusBadges.push({ label: 'Century jackpot', tone: 'bg-purple-500/15 text-purple-500 border-purple-500/30' });
  if (drawLocked)
    statusBadges.push({ label: 'Draw locked', tone: 'bg-rose-500/15 text-rose-500 border-rose-500/30' });
  else if (drawExecuted)
    statusBadges.push({ label: 'Draw executed', tone: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30' });

  return (
    <div className="rounded-md border border-border/50 bg-muted/30 p-3 space-y-3">
      {/* Identity + status badge strip */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate">
            {customName || discipline || 'Unnamed event'}
          </div>
          {customName && discipline && (
            <div className="text-xs text-muted-foreground truncate">
              {discipline}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {statusBadges.map((b) => (
            <Badge
              key={b.label}
              variant="outline"
              className={`text-[10px] font-medium border ${b.tone}`}
            >
              {b.label}
            </Badge>
          ))}
        </div>
      </div>

      {/* Grid of detail sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        <Section title="Pricing">
          <DetailRow label="Entry fee" value={formatCurrency(entryFee)} emphasize />
          <DetailRow
            label="Transaction fee"
            value={txnFee > 0 ? formatCurrency(txnFee) : 'Auto (Stripe)'}
          />
          <DetailRow label="Total to enter" value={formatCurrency(totalCost)} emphasize />
          <DetailRow
            label={isDynamicPrize ? 'Prize pool (live)' : 'Prize pool'}
            value={
              prizePool > 0 || isDynamicPrize ? formatCurrency(prizePool) : '—'
            }
            emphasize
          />
          {basePrize > 0 && (
            <DetailRow label="Base prize" value={formatCurrency(basePrize)} />
          )}
          <DetailRow
            label="Non-member surcharge"
            value={
              nonMemberSurcharge > 0
                ? formatCurrency(nonMemberSurcharge)
                : 'Use rodeo default'
            }
          />
        </Section>

        <Section title="Capacity & registration">
          <DetailRow
            label="Max contestants"
            value={
              typeof maxContestants === 'number' && maxContestants > 0
                ? maxContestants
                : 'Unlimited'
            }
          />
          <DetailRow
            label="Per performance"
            value={
              typeof perPerf === 'number' && perPerf > 0
                ? perPerf
                : 'Split across perfs'
            }
          />
          <DetailRow
            label="Registration deadline"
            value={regDeadline ? formatDate(regDeadline) : 'Open until rodeo'}
          />
          <DetailRow label="Multiple entries" value={yesNo(allowMultiReg)} />
          {allowMultiReg && (
            <DetailRow label="Max per user" value={maxPerUser ?? '—'} />
          )}
        </Section>

        <Section title="Format">
          <DetailRow label="Total rounds" value={totalRounds ?? 1} />
          <DetailRow label="Elimination" value={yesNo(eliminationEnabled)} />
          <DetailRow
            label="Event format"
            value={event?.event_format ?? '—'}
          />
          <DetailRow label="Status" value={statusDisplay ?? '—'} />
        </Section>
      </div>
    </div>
  );
}

export default EventDetailsPanel;
